import { CheckCircleIcon, XCircleIcon } from '@heroicons/react/outline'
import { has } from 'lodash'
import { useCallback, useEffect, useState } from 'react'
import ComboInput from '../ComboInput'
import { useRouter } from 'next/router'
import PhoneInput from 'react-phone-number-input/input'
import SelectBox from './SelectBox'
import MappingExampleRows from './MappingExampleRows'
import { formatToPhone } from '../../helpers/utils'

/**
 * @KNOWN_ISSUES
 * Once closed with esc, it does not upload again.
 */

export const InputRow = ({
    item,
    setValue,
    currentValue,
    mappingFields,
    errors,
    exampleRows,
    onManualModeChange,
}) => {
    const [isSelected, setIsSelected] = useState(false)
    const [selected, setSelected] = useState(null)
    const [inputType, setInputType] = useState(item.type)

    const [enterManually, setEnterManually] = useState(true)
    const [example, setExample] = useState(null)
    const [manualValue, setManualValue] = useState('')

    const key = item.name

    const isPhone = item.type === 'phone' || item.comboType === 'phone'

    const [phone, setPhone] = useState()

    const router = useRouter()
    const slug = router.query.slug

    const gettypeFromObject = () => {
        let type
        // this handles conditional view
        Object.entries(item.type).forEach(([key, value]) => {
            if (value.includes(slug)) {
                type = key
            }
        })
        if (!type) type = item.type.default
        return type
    }

    const getInputType = (obj) => {
        if (item.manually && enterManually) return isPhone ? 'phone' : 'text'

        if (typeof item.type === 'string') return item.type

        if (typeof obj !== 'object') return null

        let type = gettypeFromObject(obj)

        return type
    }

    useEffect(() => {
        setInputType(getInputType(item.type))
    }, [])

    const hasError = has(errors, item.name)

    const setMapping = useCallback(() => {
        if (
            typeof currentValue === 'string' &&
            mappingFields.includes(currentValue)
        ) {
            setSelected(currentValue)
            return
        }
        setSelected(item.alias.find((field) => mappingFields.includes(field)))
        // setSelected(() => {
        //     const fields = mappingFields.map((field) => field.toLowerCase())
        //     const index = item.alias.findIndex((field) =>
        //         fields.includes(field.toLowerCase()),
        //     )
        //     return mappingFields[index]
        // })
    }, [currentValue, item.alias, setSelected, mappingFields])

    const handleSelected = useCallback(() => {
        if (item.alias) setMapping()
    }, [setMapping, item.alias])

    useEffect(() => {
        handleSelected()
    }, [handleSelected])

    useEffect(() => {
        if (!enterManually && typeof selected === 'string' && selected.trim()) {
            setValue(key, selected)
            setIsSelected(true)
            setExample(selected)
        }
    }, [enterManually, key, selected, setValue])

    const handleSelect = (selected) => {
        selected ? setValue(key, selected) : setValue(key, null)
        selected ? setIsSelected(true) : setIsSelected(false)
        if (example !== selected) {
            setExample(selected)
        }
    }

    const handleText = ({ target }) => {
        setManualValue(target.value)
        handleSelect(target.value)
    }

    function handlePhone(value) {
        setPhone(value)
        setManualValue(value || '')
        handleSelect(value)
    }

    useEffect(() => {
        setInputType(getInputType(item.type))
        handlePhone(null)
    }, [enterManually])

    useEffect(() => {
        if (enterManually && typeof currentValue === 'string') {
            if (isPhone) {
                const phoneInputValue = currentValue.trim()
                const normalizedValue =
                    formatToPhone(phoneInputValue) || phoneInputValue
                setManualValue(normalizedValue)
                setPhone(phoneInputValue)
                setIsSelected(Boolean(phoneInputValue))
                setExample(normalizedValue)
                return
            }

            const normalizedValue = currentValue
            setManualValue(normalizedValue)
            setIsSelected(Boolean(normalizedValue))
            setExample(normalizedValue)
        }
    }, [currentValue, enterManually, isPhone])

    useEffect(() => {
        if (
            item.manually &&
            typeof currentValue === 'string' &&
            currentValue.trim() !== ''
        ) {
            const isSelectedColumn = mappingFields.includes(currentValue)
            setEnterManually(!isSelectedColumn)
        }
    }, [currentValue, item.manually, mappingFields])

    const showTodoManually = () => {
        if (!has(item, 'manually') || item.manually === false) return false

        const types = ['text', 'phone']

        if (item.type && typeof item.type === 'object') {
            const type = gettypeFromObject(item.object)
            return !types.includes(type)
        }
        return true
    }

    const allowsManualMode = showTodoManually()

    useEffect(() => {
        if (onManualModeChange) {
            onManualModeChange(item.name, allowsManualMode && !enterManually)
        }
    }, [allowsManualMode, enterManually, item.name, onManualModeChange])

    return (
        <div
            className={`grid grid-cols-[30px_1.1fr_1fr_1fr] items-start gap-[10px] border-b border-[#ECE6DC] py-[9px] ${
                hasError ? 'bg-red-50/40' : ''
            }`}>
            <div className="flex items-center justify-center pt-[8px]">
                {isSelected ? (
                    <CheckCircleIcon className="h-5 text-[#2E9E6B]" />
                ) : (
                    <XCircleIcon
                        className={`h-5 ${
                            hasError ? 'text-red-600' : 'text-gray-300'
                        } `}
                    />
                )}
            </div>
            <div className="pt-[8px] text-[13.5px] font-semibold text-[#1A1D23]">
                {item.label ? (
                    item.label
                ) : (
                    <input
                        type="text"
                        autoComplete="chrome-off"
                        placeholder="Add field name"
                        className="h-10 w-full rounded-[8px] border border-[#E1DACE] px-3 text-sm font-semibold"
                    />
                )}
            </div>
            <div className="flex items-start gap-2">
                {allowsManualMode && (
                    <div className="pt-[10px]">
                        <button
                            onClick={() => setEnterManually((value) => !value)}
                            className="text-[11.5px] font-semibold text-[#ED5C2F] hover:underline">
                            {enterManually ? 'Select column' : 'Enter Manually'}
                        </button>
                    </div>
                )}
                <div
                    className={`min-w-0 flex-1 ${
                        showTodoManually() ? 'max-w-[210px]' : ''
                    }`}>
                    {inputType === 'text' ? (
                        <input
                            data-input-name={item.name}
                            type="text"
                            value={manualValue}
                            onChange={handleText}
                            autoComplete="chrome-off"
                            className="block w-full rounded-[8px] border border-[#E1DACE] bg-white px-[14px] py-[11px] text-[13px] text-[#1A1D23] transition-all duration-150 focus:border-[#F28230] focus:outline-none focus:ring-4 focus:ring-[#F28230]/15"
                        />
                    ) : inputType === 'phone' ? (
                        <PhoneInput
                            data-input-name={item.name}
                            maxLength={14}
                            onChange={handlePhone}
                            country="US"
                            autoComplete="chrome-off"
                            className="block w-full rounded-[8px] border border-[#E1DACE] bg-white px-[14px] py-[11px] text-[13px] text-[#1A1D23] transition-all duration-150 focus:border-[#F28230] focus:outline-none focus:ring-4 focus:ring-[#F28230]/15"
                            value={phone}
                        />
                    ) : inputType === 'combo' ? (
                        <ComboInput
                            item={item}
                            defaultSelected={selected}
                            onSelect={handleSelect}
                            options={mappingFields}
                        />
                    ) : (
                        <SelectBox
                            item={item}
                            onSelect={handleSelect}
                            defaultSelected={selected}
                            options={mappingFields}
                        />
                        // <select
                        //     defaultValue={selected}
                        //     onChange={(e) => handleSelect(e.target.value)}
                        //     className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
                        // >
                        // <option value=''>Choose option</option>
                        // {
                        //     mappingFields.map((field, index) => <option key={index}>{field}</option>)
                        // }
                        // </select>
                    )}
                </div>
            </div>
            <div className="pt-[8px]">
                {(!allowsManualMode || !enterManually) &&
                    example &&
                    exampleRows &&
                    exampleRows.length > 0 && (
                        <MappingExampleRows
                            {...{
                                selected: example,
                                exampleRows,
                                isPhone,
                                enterManually:
                                    enterManually &&
                                    (inputType === 'text' ||
                                        inputType === 'phone'),
                            }}
                        />
                    )}
            </div>
        </div>
    )
}
